
import React, { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import CompanyRegistration from "./pages/CompanyRegistration";

import Welcome from "./pages/Welcome";
import HubSpotReturn from "./pages/oauth/HubSpotReturn";
import OAuthConsent from "./pages/OAuthConsent";
import Data from "./pages/Data";
import DataCleaning from "./pages/DataCleaning";
import PredictiveModels from "./pages/PredictiveModels";
import Dashboards from "./pages/Dashboards";
import Settings from "./pages/Settings";

import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import RefundPolicy from "./pages/RefundPolicy";
import Unsubscribe from "./pages/Unsubscribe";
import { FeedbackDialog } from "./components/feedback/FeedbackDialog";
import { CookieConsentBanner } from "./components/legal/CookieConsentBanner";
import { supabase } from "@/integrations/supabase/client";
import Pricing from "./pages/Pricing";
import TeamManagement from "./pages/TeamManagement";
import FolderManagement from "./pages/FolderManagement";
import DynamicDashboards from "./pages/DynamicDashboards";
import AIWorkflows from "./pages/AIWorkflows";
import Activity from "./pages/Activity";
import SmartDataEntry from "./pages/SmartDataEntry";
import Billing from "./pages/Billing";
import Profile from "./pages/Profile";
import AcceptInvite from "./pages/AcceptInvite";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import { DEV_MODE } from "@/lib/dev-mode";

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<any>(undefined); // undefined = not yet checked
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let active = true;
    const checkOnboarding = async (userId: string) => {
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("id")
          .eq("id", userId)
          .maybeSingle();
        if (active) setOnboardingComplete(!!profile);
      } catch {
        if (active) setOnboardingComplete(false);
      }
    };
    const apply = (s: any) => {
      if (!active) return;
      setSession(s);
      if (s) {
        // Deferred: running a query inside the auth callback deadlocks the auth lock.
        setTimeout(() => checkOnboarding(s.user.id), 0);
      } else {
        setOnboardingComplete(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => apply(s));
    supabase.auth.getSession().then(({ data: { session: s } }) => apply(s)).catch(() => apply(null));
    const timer = setTimeout(() => active && setTimedOut(true), 12000);

    return () => {
      active = false;
      clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, []);

  if (DEV_MODE) {
    return <>{children}</>;
  }

  // Still loading — session or onboarding not yet determined
  if (session === undefined || onboardingComplete === null) {
    if (timedOut) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-lg font-medium">This is taking longer than usual.</p>
          <p className="text-sm text-muted-foreground">Check your connection, then try again.</p>
          <button
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/welcome" replace />;
  }

  if (!onboardingComplete) {
    return <Navigate to="/company-registration" replace />;
  }

  return <>{children}</>;
};

// Create QueryClient outside of component to prevent recreation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Ensure React is available before rendering
  if (!React || typeof React.useState !== 'function') {
    return <div>Loading...</div>;
  }

  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="nextwave-ui-theme">
        <WorkspaceProvider>
        <Toaster />
        <Sonner />
          <BrowserRouter>
            <Routes>
              {/* Authentication routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/company-registration" element={<CompanyRegistration />} />
              
              {/* Main dashboard route - protected, default route */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Index />
                  </PrivateRoute>
                }
              />
              
              {/* Public marketing landing page */}
              <Route path="/welcome" element={<Welcome />} />

              {/* HubSpot per-user OAuth popup landing page */}
              <Route path="/oauth/hubspot/return" element={<HubSpotReturn />} />

              {/* Approval screen for assistants connecting to a user account */}
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              
              <Route
                path="/data"
                element={
                  <PrivateRoute>
                    <Data />
                  </PrivateRoute>
                }
              />
              <Route
                path="/smart-data-entry"
                element={
                  <PrivateRoute>
                    <SmartDataEntry />
                  </PrivateRoute>
                }
              />
              <Route
                path="/data-cleaning"
                element={
                  <PrivateRoute>
                    <DataCleaning />
                  </PrivateRoute>
                }
              />
              <Route
                path="/predictive-models"
                element={
                  <PrivateRoute>
                    <PredictiveModels />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboards"
                element={
                  <PrivateRoute>
                    <Dashboards />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <Settings />
                  </PrivateRoute>
                }
              />
              <Route path="/pricing" element={<Pricing />} />
              <Route
                path="/team-management"
                element={
                  <PrivateRoute>
                    <TeamManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/folder-management"
                element={
                  <PrivateRoute>
                    <FolderManagement />
                  </PrivateRoute>
                }
              />
              <Route path="/folders" element={<Navigate to="/folder-management" replace />} />
              <Route
                path="/activity"
                element={
                  <PrivateRoute>
                    <Activity />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ai-workflows"
                element={
                  <PrivateRoute>
                    <AIWorkflows />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dynamic-dashboards"
                element={
                  <PrivateRoute>
                    <DynamicDashboards />
                  </PrivateRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <PrivateRoute>
                    <Billing />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/payment/callback" element={<Navigate to="/billing" replace />} />
              <Route path="/payment/success" element={<Navigate to="/billing?checkout=success" replace />} />
              
              {/* Legal pages - accessible without authentication */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/terms" element={<Navigate to="/terms-of-use" replace />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              
              {/* CRM OAuth callback routes - redirect to data page with parameters */}
              <Route 
                path="/hubspot/callback" 
                element={<Navigate to="/data?crm_type=hubspot&oauth_success=true" replace />} 
              />
              <Route 
                path="/zoho/callback" 
                element={<Navigate to="/data?crm_type=zoho&oauth_success=true" replace />} 
              />
              
              {/* 404 page for non-existent routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <React.Suspense fallback={null}>
              <FeedbackDialog />
              <CookieConsentBanner />
            </React.Suspense>
          </BrowserRouter>
        </WorkspaceProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
